import { Link } from '@remix-run/react'
import { MediaType } from '#app/utils/domain/types'
import useHighlightOnHover from '#app/utils/hooks/useHighlightOnHover'
import { cn } from '#app/utils/misc'
import ActionMenu from '../ui/action-menu'
import { Icon } from '../ui/icon'

interface CardProps {
	title: string
	author?: string
	url?: string
	thumbnail: string | null
	style: 'grid-card' | 'header' | 'list-card'
}

function MediaActionMenu({
	mediaType,
	mediaId,
	hasSections,
}: {
	mediaType: MediaType
	mediaId: string
	hasSections: boolean
}) {
	const menuItems = [
		// eslint-disable-next-line react/jsx-key
		<Link to={`/remove-from-library/${mediaId}`}>
			<Icon className="text-body-sm" name="trash">
				Remove from library
			</Icon>
		</Link>,
	]
	if (mediaType === MediaType.BOOK) {
		menuItems.push(
			<Link to={`/book/${mediaId}/edit-sections`}>
				<Icon className="text-body-sm" name="reader">
					{hasSections ? 'Edit sections' : 'Define sections'}
				</Icon>
			</Link>,
		)
	}
	return (
		<ActionMenu
			triggerClassName="ml-16"
			contentClassName="min-w-40"
			menuItemContents={menuItems}
		/>
	)
}

export function UnlinkedMediaDisplayCard({
	title,
	thumbnail,
	style,
	author,
	url,
	actionMenu,
}: CardProps & { actionMenu?: React.ReactNode | null }) {
	const [ref, isHovered, highlightedClassName] = useHighlightOnHover()

	return (
		<div
			ref={ref}
			className={cn(
				'flex flex-row',
				style.includes('card') &&
					'rounded-lg border border-zinc-400 bg-slate-50 p-3 shadow-sm shadow-accent dark:bg-slate-300 dark:text-slate-900',
				style == 'grid-card' && 'shadow-md',
				style == 'grid-card' ? 'h-40' : 'h-24',
				style == 'header' && 'mb-2 mt-4',
				isHovered && style.includes('card') && highlightedClassName,
			)}
		>
			<img src={thumbnail || undefined} className="object-cover" />
			<div className="px-4">
				<p className="line-clamp-4">
					<i>{title}</i>
				</p>
				{author && <p className="text-sm line-clamp-2">{author}</p>}
				{url && (
					<a
						className="line-clamp-2 overflow-hidden text-xs text-blue-500 underline"
						href={url}
						target="_blank"
						rel="noopener noreferrer"
						onClick={(e) => e.stopPropagation()}
					>
						{url}
					</a>
				)}
			</div>
			{actionMenu}
		</div>
	)
}

export default function MediaDisplayCard({
	mediaId,
	mediaType,
	showActionMenu = false,
	enableLink = true,
	mediaHasSections = false,
	...props
}: {
	mediaId?: string
	mediaType: MediaType
	showActionMenu?: boolean
	enableLink?: boolean
	mediaHasSections?: boolean
} & CardProps) {
	let actionMenu = null
	if (mediaId && showActionMenu) {
		actionMenu = (
			<MediaActionMenu
				mediaType={mediaType}
				mediaId={mediaId}
				hasSections={mediaHasSections}
			/>
		)
	}
	return (
		<>
			{mediaId && enableLink ? (
				<Link className="m-3.5" to={`/${mediaType.toLowerCase()}/${mediaId}`}>
					<UnlinkedMediaDisplayCard
						{...props}
						actionMenu={actionMenu}
					/>
				</Link>
			) : (
				<UnlinkedMediaDisplayCard
					{...props}
					actionMenu={actionMenu}
				/>
			)}
		</>
	)
}
